import {
	CustomerControllerApiFactory,
	type DataRequestStatus,
	type EmissionValueUnit,
	ProductControllerApiFactory,
	ReceivedPactRequestControllerApiFactory,
} from '@api-client/api'
import { parseFiltersFromSearchString } from '@components/FilterButton'
import { parseSortingFromSearchString } from '@components/FilterButton/urlSearchString'
import { PageContainer } from '@components/PageLayout'
import { PageTitle } from '@components/PageTitle'
import { TabHeader } from '@components/TabHeader'
import {
	type ProductEmissionRequestSortBy,
	QueryKey,
	type ReceivedRequestSortBy,
} from '@helpers/enums'
import { useAsyncApi } from '@hooks/useAsyncApi'
import { useCatenaRequests } from '@hooks/useCatenaRequests'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet } from 'react-router-dom'

export interface ProductsOutletContext {
	refetchPurchasedProductsCount: () => void
}
export interface SelectedProducts {
	id: string
	supplierId: string
	productName: string
	status?: DataRequestStatus
	unit?: EmissionValueUnit
}

const Products = () => {
	const { t } = useTranslation()
	const productController = useAsyncApi(ProductControllerApiFactory)
	const customerController = useAsyncApi(CustomerControllerApiFactory)
	const pactRequestApi = useAsyncApi(ReceivedPactRequestControllerApiFactory)

	const [purchasedProductsSearchParams, setPurchasedProductsSearchParams] =
		useState('')
	const [receivedRequestsSearchParams, setReceivedRequestsSearchParams] =
		useState('')

	const appliedFiltersPurchased = parseFiltersFromSearchString(
		purchasedProductsSearchParams
	)
	const { sort: sortPurchased, order: orderPurchased } =
		parseSortingFromSearchString<ProductEmissionRequestSortBy>(
			purchasedProductsSearchParams
		)

	const { data, refetch: refetchPurchasedProductsCount } = useQuery({
		queryKey: [
			QueryKey.GetPurchasedProducts,
			0,
			10,
			sortPurchased,
			orderPurchased,
			appliedFiltersPurchased,
		],
		queryFn: () =>
			productController(api =>
				api.getPurchasedProducts(
					undefined,
					undefined,
					undefined,
					appliedFiltersPurchased.selectedSuppliers,
					appliedFiltersPurchased.lastUpdated?.from,
					appliedFiltersPurchased.lastUpdated?.to,
					undefined,
					undefined,
					appliedFiltersPurchased.productRequestStatus,
					appliedFiltersPurchased.emissionDataVerified,
					appliedFiltersPurchased.emissionValue?.from,
					appliedFiltersPurchased.emissionValue?.to,
					appliedFiltersPurchased.emissionValueConfidence,
					appliedFiltersPurchased.emissionValueGranularity,
					appliedFiltersPurchased.shareOfPrimaryDataInEmissionFactor?.from,
					appliedFiltersPurchased.shareOfPrimaryDataInEmissionFactor?.to,
					sortPurchased,
					orderPurchased,
					undefined
				)
			),
	})

	const appliedFiltersReceived = parseFiltersFromSearchString(
		receivedRequestsSearchParams
	)
	const { sort: sortReceived, order: orderReceived } =
		parseSortingFromSearchString<ReceivedRequestSortBy>(
			receivedRequestsSearchParams
		)

	const { data: dataReceived } = useQuery({
		queryKey: [
			QueryKey.GetReceivedProductRequests,
			0,
			10,
			sortReceived,
			orderReceived,
			appliedFiltersReceived,
		],
		queryFn: () =>
			productController(api =>
				api.getReceivedProductRequests(
					0,
					10,
					appliedFiltersReceived.selectedCustomers,
					appliedFiltersReceived.productRequestStatus,
					sortReceived,
					orderReceived,
					appliedFiltersReceived.lastUpdated?.from,
					appliedFiltersReceived.lastUpdated?.to,
					undefined
				)
			),
	})

	const { data: dataPactRequests } = useQuery({
		queryKey: [QueryKey.ListReceivedRequests],
		queryFn: () =>
			pactRequestApi(api => api.listReceivedRequests()).then(r => r.data),
	})

	const { data: catenaRequestsData } = useCatenaRequests()

	// TODO(pascal): Confirm with design whether or not this initial
	// call is required
	useQuery({
		queryKey: [QueryKey.GetCustomers],
		queryFn: () => customerController(api => api.listCustomers(0, 500)),
	})

	return (
		<PageContainer>
			<PageTitle text={t('products')} />
			<TabHeader
				purchasedProductsSearch={purchasedProductsSearchParams}
				setPurchasedProductsSearch={setPurchasedProductsSearchParams}
				setReceivedRequestsSearchParams={setReceivedRequestsSearchParams}
				purchasedProductsCount={data?.data.metadata.count}
				receivedProductRequestsCount={dataReceived?.data?.metadata.count}
				receivedProductRequestsSearch={receivedRequestsSearchParams}
				pactRequestsCount={dataPactRequests?.data.length}
				catenaRequestsCount={catenaRequestsData?.data.length}
			/>
			<Outlet context={{ refetchPurchasedProductsCount }} />
		</PageContainer>
	)
}

export { Products }
